import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getDateLocale } from '../i18n';
import { getTenantSubdomain } from '../services/api';
import { RS_CHART_BASE, rsLineAreaGradient } from '../utils/rsChartPalette';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TAB_KEYS = ['occupation_brut', 'production_client', 'efficacite', 'contraintes'];
const TAB_LABEL_KEYS = {
  occupation_brut: 'tabs.occupationBrut',
  production_client: 'tabs.productionClient',
  efficacite: 'tabs.efficacite',
  contraintes: 'tabs.contraintes',
};
const CHART_DESC_KEYS = {
  occupation_brut: 'chartDesc.occupationBrut',
  production_client: 'chartDesc.productionClient',
  efficacite: 'chartDesc.efficacite',
  contraintes: 'chartDesc.contraintes',
};
const FORMULA_DESC_KEYS = {
  occupation_brut: 'formulaDesc.occupationBrut',
  production_client: 'formulaDesc.productionClient',
  efficacite: 'formulaDesc.efficacite',
  contraintes: 'formulaDesc.contraintes',
};
const TAUX_KEYS = {
  occupation_brut: 'taux_occupation_brut',
  production_client: 'taux_production_client',
  efficacite: 'taux_efficacite_operationnelle',
  contraintes: 'taux_contraintes_operationnelles',
};
const COUNT_KEYS = {
  occupation_brut: 'count_occupation_brut',
  production_client: 'count_production_client',
  efficacite: null,
  contraintes: 'count_contraintes',
};
const VALUE_LABEL = {
  occupation_brut: '',
  production_client: '',
  efficacite: '%',
  contraintes: '',
};

const makeDefaultFormatChartLabel = (dateLocale) => (dateStr, dayStr, period) => {
  if ((period === 'Today' || period === 'Yesterday') && dayStr && dayStr.includes(':')) return dayStr;
  if (dayStr && (dayStr.includes(' - ') || dayStr.match(/^[A-Z][a-z]{2} \d{4}$/))) return dayStr;
  if (!dateStr) return dayStr || '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return dayStr || dateStr || '';
    switch (period) {
      case 'Today':
      case 'Yesterday':
        return dayStr || date.toLocaleDateString(dateLocale, { weekday: 'short' });
      case 'Last 7 Days':
        return dayStr || date.toLocaleDateString(dateLocale, { weekday: 'short' });
      case 'Last 30 Days':
        return date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
      case 'This Quarter':
        if (dayStr && dayStr.includes(' - ')) return dayStr;
        const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
        const quarterStart = new Date(date.getFullYear(), quarterStartMonth, 1);
        const daysDiff = Math.floor((date - quarterStart) / (1000 * 60 * 60 * 24));
        return `W${Math.floor(daysDiff / 7) + 1}`;
      case 'YTD':
        if (dayStr && dayStr.match(/^[A-Z][a-z]{2} \d{4}$/)) return dayStr;
        return date.toLocaleDateString(dateLocale, { month: 'short' });
      case 'Custom Range':
        if (dayStr && (dayStr.includes(' - ') || dayStr.match(/^[A-Z][a-z]{2} \d{4}$/))) return dayStr;
        return date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
    }
  } catch {
    return dayStr || dateStr || '';
  }
};

const OccupationRatesChartCard = ({
  data = null,
  filters = {},
  loading = false,
  formatChartLabel,
}) => {
  const { t, i18n } = useTranslation('manager');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [activeTab, setActiveTab] = useState(TAB_KEYS[0]);
  const period = filters?.period || 'Last 30 Days';
  const dateLocale = getDateLocale(i18n.language);
  const resolvedFormatChartLabel = formatChartLabel || makeDefaultFormatChartLabel(dateLocale);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
    const lineColor = isRsTenant ? RS_CHART_BASE : '#1162d4';
    const labels = data.map((item) =>
      resolvedFormatChartLabel(item.date, item.day, period)
    );
    const countKey = COUNT_KEYS[activeTab];
    const values = data.map((item) => {
      if (activeTab === 'efficacite') {
        const pc = Number(item.count_efficacite_pc);
        const co = Number(item.count_efficacite_co);
        let val = (Number.isFinite(pc) ? pc : 0) + (Number.isFinite(co) ? co : 0);
        if (val > 0 && val <= 1) val = val * 100;
        return val;
      }
      const n = Number(item[countKey]);
      return Number.isFinite(n) ? n : 0;
    });
    return {
      labels,
      datasets: [
        {
          label: t(TAB_LABEL_KEYS[activeTab]),
          data: values,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return isRsTenant ? 'rgba(42, 42, 42, 0.2)' : 'rgba(17, 98, 212, 0.2)';
            if (isRsTenant) return rsLineAreaGradient(ctx, chartArea);
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(17, 98, 212, 0.4)');
            gradient.addColorStop(0.5, 'rgba(17, 98, 212, 0.1)');
            gradient.addColorStop(1, 'rgba(17, 98, 212, 0.03)');
            return gradient;
          },
          borderColor: lineColor,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: lineColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        },
      ],
    };
  }, [data, activeTab, period, resolvedFormatChartLabel, t]);

  const chartOptions = useMemo(
    () => {
      const textColor = isDark ? '#94a3b8' : '#617289';
      const gridColor = isDark ? '#334155' : '#f0f2f4';
      const tooltipBg = isDark ? '#1e293b' : '#111418';
      const isEfficiency = activeTab === 'efficacite';

      const formatAsPercent = (raw) => {
        const v = Number(raw);
        if (!Number.isFinite(v)) return '0 %';
        const pct = isEfficiency ? v : v * 100;
        return `${Number(pct).toFixed(1)} %`;
      };

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: tooltipBg,
            padding: 12,
            titleFont: { size: 12, weight: 'bold' },
            bodyFont: { size: 11 },
            callbacks: {
              label: (context) => {
                const y = Number(context.parsed.y);
                const val = Number.isFinite(y) ? y : 0;
                return `${context.dataset.label || ''}: ${formatAsPercent(val)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, font: { size: 11 } },
          },
          y: {
            min: 0,
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => {
                const n = Number(value);
                if (!Number.isFinite(n)) return '0 %';
                return formatAsPercent(n);
              },
            },
          },
        },
      };
    },
    [isDark, activeTab]
  );

  if (loading) {
    return (
      <div className="w-full bg-white dark:bg-slate-800 p-6 rounded-xl border border-[#e5e7eb] dark:border-slate-600 shadow-sm transition-colors duration-200">
        <div className="h-8 w-48 rounded bg-[#f0f2f4] dark:bg-slate-700 animate-pulse mb-6" />
        <div className="relative overflow-hidden rounded-lg bg-[#f9fafb] dark:bg-slate-700/50" style={{ minHeight: '280px' }}>
          <div className="absolute inset-0 animate-pulse bg-[#f0f2f4]/60 dark:bg-slate-600/40" />
        </div>
      </div>
    );
  }

  const hasData = data && data.length > 0;

  return (
    <div className="w-full bg-white dark:bg-slate-800 p-6 rounded-xl border border-[#e5e7eb] dark:border-slate-600 shadow-sm flex flex-col transition-colors duration-200">
      <div className="mb-6">
        <h3 className="text-[#111418] dark:text-slate-100 text-lg font-bold">{t('occupancyTitle')}</h3>
        <p className="text-[#617289] dark:text-slate-400 text-xs mt-1">
          {t(CHART_DESC_KEYS[activeTab])}
        </p>
      </div>

      {hasData ? (
        <>
          <div className="w-full mb-6">
            <nav className="relative flex w-full border-b border-[#e5e7eb] dark:border-slate-600">
              {TAB_KEYS.map((tabKey) => (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => setActiveTab(tabKey)}
                  className={`flex-1 min-w-0 px-2 py-2.5 font-medium text-xs sm:text-sm transition-colors ${
                    activeTab === tabKey ? 'text-primary' : 'text-[#617289] dark:text-slate-400 hover:text-[#111418] dark:hover:text-slate-100'
                  }`}
                >
                  {t(TAB_LABEL_KEYS[tabKey])}
                </button>
              ))}
              <motion.div
                className="absolute bottom-0 h-0.5 bg-primary"
                style={{ width: `${100 / TAB_KEYS.length}%` }}
                initial={false}
                animate={{ left: `${TAB_KEYS.indexOf(activeTab) * (100 / TAB_KEYS.length)}%` }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            </nav>
          </div>

          <div className="flex-1 relative overflow-hidden" style={{ minHeight: '240px', maxHeight: '360px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="absolute inset-0 w-full h-full"
              >
                <Line data={chartData} options={chartOptions} />
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="text-center text-[#9ca3af] dark:text-slate-500 text-xs mt-4 leading-tight">
            {t(FORMULA_DESC_KEYS[activeTab])}
          </p>
        </>
      ) : (
        <div
          className="flex items-center justify-center text-[#617289] dark:text-slate-400 text-sm py-12"
          style={{ minHeight: '240px' }}
        >
          {t('noData')}
        </div>
      )}
    </div>
  );
};

export default OccupationRatesChartCard;
